/**
 * Service for fetching application configuration from the backend API
 */

import packageJson from '../../package.json';

export interface ClientConfigResponse {
  tokenUrl: string;
  clientId: string;
  scope: string;
  oktaIssuer: string;
  reactClientId: string;
  appVersion: string;
  menderSsoGuid: string;
  pepsenseUrl: string;
  clusterDetails: string;
  clusterZone: string;
  msalClientId: string;
  msalTenantId: string;
}

const CONFIG_API_URL = '/api/v2/config/clientConfigs';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Attempt to fetch client config once
 */
async function attemptFetch(
  attemptNumber: number
): Promise<ClientConfigResponse> {
  const response = await fetch(CONFIG_API_URL, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch Okta config: ${response.status} ${response.statusText}`
    );
  }

  const config: ClientConfigResponse = await response.json();

  // Validate required fields for Okta authentication
  if (
    !config.tokenUrl ||
    !config.clientId ||
    !config.scope ||
    !config.oktaIssuer ||
    !config.reactClientId
  ) {
    throw new Error(
      'Invalid config response: missing required Okta fields (tokenUrl, clientId, scope, oktaIssuer, or reactClientId)'
    );
  }

  // Fallback: If API returns placeholder, use package.json version
  if (config.appVersion === '$npm_package_version' || !config.appVersion) {
    config.appVersion = packageJson.version;
  }

  return config;
}

/**
 * Recursive retry logic
 */
async function fetchWithRetry(
  attemptNumber: number,
  previousErrors: Error[] = []
): Promise<ClientConfigResponse> {
  try {
    return await attemptFetch(attemptNumber);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));

    const allErrors = [...previousErrors, err];

    if (attemptNumber >= MAX_RETRIES) {
      const lastError = allErrors[allErrors.length - 1];
      const errorMessage = `Unable to fetch client configuration after ${MAX_RETRIES} attempts. ${
        lastError?.message || 'Unknown error'
      }`;
      throw new Error(errorMessage);
    }

    await sleep(RETRY_DELAY * attemptNumber);

    return fetchWithRetry(attemptNumber + 1, allErrors);
  }
}

/**
 * Fetch client configuration from the backend API with retry logic
 * Returns all environment configuration including Okta, MSAL, and other client settings
 * @returns Promise<ClientConfigResponse>
 * @throws Error if config cannot be fetched after retries
 */
export async function fetchClientConfig(): Promise<ClientConfigResponse> {
  return fetchWithRetry(1);
}
